import React, { useEffect, useContext, useState, useCallback, useRef } from 'react';

import PresentPC from 'components/pages/Skill/PresentPC';
import PresentSP from 'components/pages/Skill/PresentSP';

import { CHOICES_MAP } from 'utils/constant';
import { 
  isSchemaValid,
  isCustomValid,
  useWindowDimensions,
  calcSalaryCoefficient,
  useDirectTransCheck,
  isFatalApiError,
  useIECheck,
  sortErrors,
  postHeightToParent,
} from 'utils';

import AppContext from 'fluxes/context';
import { UPDATE_SKILLS, CHANGE_VALID } from 'fluxes/actions';
import { getHourSalaryApi } from 'utils/api';
import { CountUp } from 'countup.js';

const Skill = () => {
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);
  useDirectTransCheck({ state });
  const [ experience, setExperience ] = useState(state.experience);
  const [ qualification, setQualification ] = useState(state.qualification);
  const [ qualificationText, setQualificationText ] = useState(state.qualificationText);
  
  const [ suggestHourSalary, setSuggestHourSalary ] = useState(state.suggestHourSalary);
  const [ isApiError, setIsApiError ] = useState(false);
  const [ hourSalaryLoading, setHourSalaryLoading ] = useState(state.hourSalaryLoading);
  const [ hasQualificationText, setHasQualificationText ] = useState(state.hasQualificationText);

  const { ekiCd } = state;
  const [ apiCalledEkiCd, setApiCalledEkiCd ] = useState('');
  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;
  const pageEl = useRef(null);
  
  // 入力チェック
  const initialValid = { valid: state.validMap[path], errorMessages: [] };
  const [ valids, setValids ] = useState({
    schema: initialValid,
    checkAnyQualifications: initialValid,
  });
  const [ isShowError, setIsShowError ] = useState({
    qualificationText: false,
  });

  const valid = Object.keys(valids).every(key => valids[key].valid);
  const dispErrors = useCallback(() => {
    const { schema, checkAnyQualifications } = valids;
    const errors = sortErrors([
      ...schema.errorMessages,
      ...checkAnyQualifications.errorMessages,
    ]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if(!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    return errorMap;
  }, [ isShowError, valids ]);

  // エラーチェック・時給サジェスト
  useEffect(()=> {
    
    const validateion = async () => {
      // エラーチェックを実行
      const [ isValid, newErrorMessages ] = isSchemaValid({
        target: {
          experience,
          qualification,
          qualificationText,
          suggestHourSalary,
        },
      });
      const newValid = { schema: { valid: isValid, errorMessages: newErrorMessages } };
      setValids(org => ({ ...org, ...newValid }));
    };   
    validateion();

    // 時給サジェスト
    const handleChangeSkills = async () => {
      if(apiCalledEkiCd !== ekiCd){
        setHourSalaryLoading(true);
        const params = { ekiCd };
        const hourSalary = await getHourSalaryApi({ params }).catch(err => {
          // FATELエラーチェック
          const isFatal = isFatalApiError(err);
          setIsApiError(isFatal);
          if(isFatal) throw err;
          return 0;
        });
        setHourSalaryLoading(false);
        setApiCalledEkiCd(ekiCd);
        setSuggestHourSalary(hourSalary);
      };

      // CountUp.jsで金額じゃかじゃかをDOM直接更新
      const coefficient = calcSalaryCoefficient(qualification, qualificationText, experience); // 経験と資格による係数を取得
      const options = {
        startVal: 0,
        duration: 0.5, 
      };
      const min = new CountUp('suggestHourSalaryMin', suggestHourSalary + coefficient.min, options);
      const max = new CountUp('suggestHourSalaryMax', suggestHourSalary + coefficient.max, options);
      if (!min.error && !max.error) {
        min.start();
        max.start();
      } else {
        console.error(min.error, max.error);
      }
    };
  
    handleChangeSkills();
    
  }, [ experience, qualification, hasQualificationText, ekiCd, suggestHourSalary, qualificationText, apiCalledEkiCd ]);

  // カスタムチェック 介護資格いずれか必須
  useEffect(()=>{
    const validation = async () => {
      // エラーチェックを実行
      const [ isValid, newErrorMessages ] = await isCustomValid({
        checks: [ 'checkAnyQualifications' ],
        target: { qualification, qualificationText },
      });
      console.log('valid:custom:checkAnyQualifications', { isValid, newErrorMessages });
      // erros更新
      const newValid = { checkAnyQualifications: { valid: isValid, errorMessages: newErrorMessages } };
      setValids(org => ({ ...org, ...newValid }));
    };
    validation();
  }, [ qualification, qualificationText ]);

  // LP埋め込み時の高さ送信
  useEffect(() => {
    if(state.lpMode){
      postHeightToParent(state.lpOrigin, pageEl.current.clientHeight);
    }
  }, [ state.lpMode, state.lpOrigin, pageEl ]);

  // 介護経験有無の変更
  const handleChangeExperience = ( e ) =>{
    setExperience(e.target.value);
  };

  // 介護経験資格の変更
  const handleChangeQualification = ( e ) => {
    const val = e.target.value;
    if (qualification.includes(val)) {
      setQualification(qualification.filter(item => item !== val));
    } else {
      setQualification([ ...qualification, val ]);
    }
    // qualificationTextエラー表示開始
    if(!isShowError.qualificationText) {
      setIsShowError({ ...isShowError, qualificationText: true });
    }
  };

  // 介護資格(手入力)の変更
  const handleQualificationText = ( e ) => {
    const val = e.target.value;
    setQualificationText(val);
      
    // 入力値の有無
    if(val.length > 0 ){
      setHasQualificationText(true);
    }else{
      setHasQualificationText(false);
    }
    // qualificationTextエラー表示開始
    if(!isShowError.qualificationText) {
      setIsShowError({ ...isShowError, qualificationText: true });
    }
  };


  // 画面項目の作成
  const { experienceList, qualificationList } = CHOICES_MAP;
  const experienceObject = { title: '介護経験有無', onChange: handleChangeExperience, data: experience, dataList: experienceList };
  const qualificationObject = { title: '介護資格', onChange: handleChangeQualification, data: qualification, dataList: qualificationList };
  const qualificationTextObject = { onChange: handleQualificationText, data: qualificationText };

  // ボタン押下
  // state更新処理
  const updateState = () => {
    dispatch({
      type: UPDATE_SKILLS,
      updSkill: {
        experience,
        qualification,
        qualificationText,
        suggestHourSalary,
        hasQualificationText,
      },
    });
  };
  const onClickMainButton = {
    prev: () => {
      updateState();
    },
    next: () => {
      updateState();
      dispatch({
        type: CHANGE_VALID, 
        path,
        valid,
      });
    },
  };
  
  return(
    <div id='pageSkill' ref={pageEl}>
      { isSp ? (
        <PresentSP 
          onClickMainButton={onClickMainButton}
          valid={valid}
          experienceObject={experienceObject}
          qualificationObject={qualificationObject}
          qualificationTextObject={qualificationTextObject}
          isApiError={isApiError}
          hourSalaryLoading={hourSalaryLoading}
          errorDispMap={dispErrors()}
          lpMode={state.lpMode}
        />
      ) : (
        <PresentPC 
          onClickMainButton={onClickMainButton}
          valid={valid}
          experienceObject={experienceObject}
          qualificationObject={qualificationObject}
          qualificationTextObject={qualificationTextObject}
          isApiError={isApiError}
          hourSalaryLoading={hourSalaryLoading}
          errorDispMap={dispErrors()}
          lpMode={state.lpMode}
        />
      )}
    </div>
  );
};

export default Skill;